import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { decrypt } from "../../Encrypdecryp/Crypt";
import JsonEditor from "../../components/JsonEditor";
import PdfPreview from "../../components/PdfPreview.js";
import ExtractedData from "../../components/ExtractedData";
import { debounce } from "lodash";
import "./DocumentManager.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error(
    "API_BASE_URL is not defined in .env"
  );
}

const DocumentManager = ({ collapsed = false, theme = "dark" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState("normal");
  const [documents, setDocuments] = useState([]);
  const [validations, setValidations] = useState([]);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isFetchingValidations, setIsFetchingValidations] = useState(false);
  const [validationStep, setValidationStep] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(5);
  const [averageMonthlyImportAmount, setAverageMonthlyImportAmount] =
    useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isAuthenticated = !!localStorage.getItem("authToken");

  const transactionId =
    location.state?.transactionId ||
    new URLSearchParams(location.search).get("transactionId") ||
    "";
  let rawTransactionName = location.state?.transactionName || "Transaction";
  const cleanedTransactionName =
    rawTransactionName
      .replace(new RegExp(`\\b${transactionId}\\b`, "g"), "")
      .replace(/^Transaction\s+|\s+Transaction\s*$/g, "Transaction")
      .trim() || "Transaction";
  const transactionName =
    cleanedTransactionName === "" ? "Transaction" : cleanedTransactionName;
  const fromRuntimeValidation = location.state?.fromRuntimeValidation || false;
  const incomingDocuments = location.state?.documents || [];
  const incomingValidations = location.state?.validations || [];
  const validationId = location.state?.validationId;

  // Cleanup for pdfUrl to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    let timer;
    if (timerActive && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            setValidationStep(1);
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerActive, timeRemaining]);

  useEffect(() => {
    console.log('DocumentManager transactionId:', transactionId);
    if (!isAuthenticated) {
      toast.error("No authentication token found. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      navigate("/login");
    }
  }, [isAuthenticated, navigate, theme]);

  useEffect(() => {
    if (incomingValidations.length > 0) {
      console.log("Incoming Validations:", incomingValidations);
      const formattedValidations = incomingValidations.map((v) => {
        console.log(`Validation ID: ${v.id}, Status: ${v.status}`);
        const normalizedStatus =
          v.status === "FAILED"
            ? "Fail"
            : v.status === "PASSED"
            ? "Pass"
            : v.status || "N/A";
        return {
          id: v.id,
          transactionId: v.transaction_set_id,
          transactionName: v.transactionName || transactionName,
          checkName: v.validation_check || "Validation Details",
          status: normalizedStatus,
          lastRun: v.last_run || "N/A",
          evidence: Array.isArray(v.reason_evidence)
            ? v.reason_evidence.join("; ")
            : v.reason_evidence?.discrepancies?.join("; ") || "",
          details:
            normalizedStatus === "Pass"
              ? "Validation passed successfully"
              : Array.isArray(v.reason_evidence)
              ? v.reason_evidence[0] || "Validation failed"
              : v.reason_evidence?.discrepancies?.[0] || "Validation failed",
          documentIds: v.document_ids,
          progress: 100,
          changeHistory: v.changeHistory || [],
          highlightFields: v.highlightFields || [],
          module: v.module || 3,
        };
      });
      setValidations(formattedValidations);
      if (formattedValidations.length > 0) {
        setValidationStep(1);
      }
    }
  }, [incomingValidations, transactionName]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const isSuperuser = response.data.is_superuser === "True";
        setUserRole(isSuperuser ? "admin" : "normal");
      } catch (err) {
        console.error("Error fetching user role:", err);
        setUserRole("normal");
        toast.error(err.message || "Unable to fetch user role.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: theme === "dark" ? "#f8d7da" : "#fee2e2",
            color: "#721c24",
            borderRadius: "8px",
            fontSize: "14px",
          },
        });
      }
    };
    fetchUserRole();
  }, [theme]);

  const fetchThumbnail = async (filename) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await axios.get(
        `${API_BASE_URL}/thumbnails/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error fetching thumbnail:", error);
      return "/default-thumbnail.jpg";
    }
  };

  const getThumbnailFilename = (pdfPath) => {
    if (pdfPath) {
      const fileName =
        pdfPath.split("/storage/")[1] || pdfPath.split("/").pop();
      return fileName.replace(/\.pdf$/, ".jpg");
    }
    return null;
  };

  useEffect(() => {
    if (
      fromRuntimeValidation &&
      incomingDocuments.length > 0 &&
      transactionId
    ) {
      const initDocuments = async () => {
        setLoading(true);
        try {
          const formattedDocuments = await Promise.all(
            incomingDocuments.map(async (doc) => {
              console.log('Document ID:', doc.id);
              const filename = getThumbnailFilename(doc.pdf_path);
              const thumbnail = filename
                ? await fetchThumbnail(filename)
                : "/default-thumbnail.jpg";
              return {
                id: doc.id.toString(),
                name: doc.pdf_file_name || doc.pdf_path.split("/").pop(),
                docType: decrypt(doc.type) || "Unknown",
                pdfUrl: null,
                pdfPath: doc.pdf_path,
                thumbnail,
                extractedData: null,
                documentId: doc.id.toString(),
              };
            })
          );
          setDocuments(formattedDocuments);
        } catch (error) {
          console.error("Error initializing documents:", error);
          toast.error(error.message || "Unable to process documents.", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
              background: theme === "dark" ? "#f8d7da" : "#fee2e2",
              color: "#721c24",
              borderRadius: "8px",
              fontSize: "14px",
            },
          });
          navigate("/dashboard");
        } finally {
          setLoading(false);
        }
      };
      initDocuments();
    } else {
      toast.error("No documents provided for this transaction.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      navigate("/dashboard");
    }
  }, [fromRuntimeValidation, incomingDocuments, transactionId, navigate, theme]);

  useEffect(() => {
    if (documents.length > 0 && selectedFileIndex === null) {
      setSelectedFileIndex(0);
      handleFileSelect(0);
    }
  }, [documents, selectedFileIndex]);

  const handleFileSelect = async (index) => {
    if (isEditing && !isSaved) {
      toast.warn("Unsaved changes will be lost. Please save or cancel before switching documents.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#fff3cd" : "#fef3c7",
          color: "#856404",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      return;
    }

    if (index === selectedFileIndex) {
      console.log('Same file selected, skipping API calls');
      return;
    }

    console.log('Selected documentId:', documents[index]?.id);
    if (!documents[index]) {
      toast.error("The selected file could not be found.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      setSelectedFileIndex(null);
      setPdfUrl(null);
      setExtractedData(null);
      setIsSaved(false);
      return;
    }
    setSelectedFileIndex(index);
    setLoading(true);
    setError(null);
    setIsSaved(false);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const fileName = documents[index].pdfPath.split("/storage/")[1];
      const pdfResponse = await axios.get(
        `${API_BASE_URL}/storage/${fileName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      const newPdfUrl = URL.createObjectURL(pdfResponse.data);
      setPdfUrl(newPdfUrl);

      const dataResponse = await axios.get(
        `${API_BASE_URL}/transactions/${transactionId}/documents/${documents[index].id}/mongo-data`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setExtractedData(dataResponse.data || null);

      if (fromRuntimeValidation && validationId) {
        setIsFetchingValidations(true);
        const validationResponse = await axios.get(
          `${API_BASE_URL}/documents/validations/${transactionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const fetchedValidations = (
          Array.isArray(validationResponse.data)
            ? validationResponse.data
            : validationResponse.data.data || []
        ).map((v) => {
          console.log(`Fetched Validation ID: ${v.id}, Status: ${v.status}`);
          const normalizedStatus =
            v.status === "FAILED"
              ? "Fail"
              : v.status === "PASSED"
              ? "Pass"
              : v.status || "N/A";
          return {
            id: v.id,
            transactionId: v.transaction_set_id,
            transactionName: v.transactionName || transactionName,
            checkName: v.validation_check || "Validation Details",
            status: normalizedStatus,
            lastRun: v.last_run || "N/A",
            evidence: Array.isArray(v.reason_evidence)
              ? v.reason_evidence.join("; ")
              : v.reason_evidence?.discrepancies?.join("; ") || "",
            details:
              normalizedStatus === "Pass"
                ? "Validation passed successfully"
                : Array.isArray(v.reason_evidence)
                ? v.reason_evidence[0] || "Validation failed"
                : v.reason_evidence?.discrepancies?.[0] || "Validation failed",
            documentIds: v.document_ids,
            progress: 100,
            changeHistory: v.changeHistory || [],
            highlightFields: v.highlightFields || [],
            module: v.module || 3,
          };
        });
        setValidations(fetchedValidations);
        const validation = fetchedValidations.find(
          (v) => v.id.toString() === validationId.toString()
        );
        setSelectedValidation(validation || null);
        setIsFetchingValidations(false);
      }
    } catch (error) {
      console.error("Error fetching PDF or data:", error);
      let errorMessage =
        "Failed to load PDF or extracted data: " +
        (error.response?.data?.message || error.message || "Unknown error");
      if (error.response?.status === 403) {
        errorMessage =
          "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      setError(errorMessage);
      setPdfUrl(null);
      setExtractedData(null);
      setIsSaved(false);
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const getFinalizedData = (data) => {
    if (!data || typeof data !== "object") return null;
    if (data.mongo_data && data.mongo_data.finalized_data) {
      return getFinalizedData(data.mongo_data.finalized_data);
    }
    return data.finalized_data || data || null;
  };

  const debouncedHandleJsonChange = debounce((newJson) => {
    setExtractedData(newJson);
    setIsSaved(false);
    setIsEditing(true);
  }, 500);

  const handleJsonChange = (newJson) => {
    debouncedHandleJsonChange(newJson);
  };

  const handleUpdateData = async () => {
    if (selectedFileIndex === null || !extractedData) {
      toast.warn("Please select a file and ensure data is uploaded.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#fff3cd" : "#fef3c7",
          color: "#856404",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      return;
    }
    if (!transactionId || !documents[selectedFileIndex]?.id) {
      toast.error("Transaction ID or Document ID is missing.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const documentId = documents[selectedFileIndex].id;
      const url = `${API_BASE_URL}/transactions/${transactionId}/documents/${documentId}/update-data`;
      const response = await axios.put(
        url,
        { finalized_data: extractedData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Update response:", response.data);
      toast.success("Data updated successfully.", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#d4edda" : "#d1fae5",
          color: "#155724",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
      const updatedDocuments = documents.map((doc, index) =>
        index === selectedFileIndex ? { ...doc, extractedData } : doc
      );
      setDocuments(updatedDocuments);
      setIsSaved(true);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating data:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error headers:", error.response?.headers);
      let errorMessage =
        "Failed to update data: " +
        (error.response?.data?.message || error.message || "Unknown error");
      if (error.response?.status === 405) {
        errorMessage =
          "The server does not support this update method. Please check the API endpoint or contact support.";
      } else if (error.response?.status === 404) {
        errorMessage =
          "The update endpoint was not found. Please verify the API configuration.";
      } else if (error.response?.status === 403) {
        errorMessage =
          "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    }
  };

  const handleRerunValidations = async () => {
    toast.info("Rerunning all validations for the transaction...", {
      position: "bottom-right",
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: {
        background: theme === "dark" ? "#e9ecef" : "#f3f4f6",
        color: "#495057",
        borderRadius: "8px",
        fontSize: "14px",
      },
    });

    setIsValidating(true);
    setTimerActive(true);
    setTimeRemaining(5);
    try {
      const token = localStorage.getItem("authToken");
      if (!token)
        throw new Error(
          "Access denied: Invalid or expired token. Please log in again."
        );
      const response = await axios.post(
        `${API_BASE_URL}/documents/validate-all`,
        { transaction_set_id: parseInt(transactionId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fetchedValidations = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
      setValidations(fetchedValidations);

      toast.success("All validations have been rerun successfully.", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#d4edda" : "#d1fae5",
          color: "#155724",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } catch (err) {
      console.error("Error running validations:", err);
      let errorMessage =
        "Failed to run validations: " + (err.message || "Unknown error");
      if (err.response?.status === 403) {
        errorMessage =
          "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleViewValidations = async () => {
    setIsFetchingValidations(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Access denied: Invalid or expired token.");
      const response = await axios.get(
        `${API_BASE_URL}/documents/validations/${transactionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fetchedValidations = (
        Array.isArray(response.data) ? response.data : response.data.data || []
      ).map((v) => {
        console.log(`Fetched Validation ID: ${v.id}, Status: ${v.status}`);
        const normalizedStatus =
          v.status === "FAILED"
            ? "Fail"
            : v.status === "PASSED"
            ? "Pass"
            : v.status || "N/A";
        return {
          id: v.id,
          transactionId: v.transaction_set_id,
          transactionName: v.transactionName || transactionName,
          checkName: v.validation_check || "Validation Details",
          status: normalizedStatus,
          lastRun: v.last_run || "N/A",
          evidence: Array.isArray(v.reason_evidence)
            ? v.reason_evidence.join("; ")
            : v.reason_evidence?.discrepancies?.join("; ") || "",
          details:
            normalizedStatus === "Pass"
              ? "Validation passed successfully"
              : Array.isArray(v.reason_evidence)
              ? v.reason_evidence[0] || "Validation failed"
              : v.reason_evidence?.discrepancies?.[0] || "Validation failed",
          documentIds: v.document_ids,
          progress: 100,
          changeHistory: v.changeHistory || [],
          highlightFields: v.highlightFields || [],
          module: v.module || 3,
        };
      });

      setValidations(fetchedValidations);

      navigate(`/runtime-validation?transactionId=${transactionId}&module=0`, {
        state: {
          validations: fetchedValidations,
          transactionId,
          transactionName,
          documents: documents,
        },
      });

      toast.success("Validation results have been loaded.", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#d4edda" : "#d1fae5",
          color: "#155724",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } catch (err) {
      console.error("Error fetching validations:", err);
      let errorMessage =
        "Failed to fetch validations: " + (err.message || "Unknown error");
      if (err.response?.status === 403) {
        errorMessage =
          "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === "dark" ? "#f8d7da" : "#fee2e2",
          color: "#721c24",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } finally {
      setIsFetchingValidations(false);
    }
  };

  const truncateFileName = (name, maxLength = 25) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + "...";
  };

  return (
    <Box
      className={`document-main ${theme}-theme`}
      sx={{
        display: "flex",
        flexDirection: "column",
        transition: "margin-left 0.3s ease, width 0.3s ease",
        height: "auto",
        overflow: "visible",
        backgroundColor: "var(--background-color)",
      }}
    >
      <div className="particles">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme}
      />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: { xs: "10px", sm: "15px", md: "20px" },
        }}
      >
        {error && (
          <Typography
            sx={{
              mb: "20px",
              fontSize: "0.875rem",
              color: "var(--error-color)",
            }}
          >
            {error}
          </Typography>
        )}
        <Box className="validation-card">
          {selectedValidation ? (
            <>
              <Typography
                variant="h6"
                className="validation-card-title"
                sx={{ color: "var(--text-color)" }}
              >
                {selectedValidation.checkName}
              </Typography>
              <Box className="validation-card-content">
                <Box className="validation-card-item">
                  <Typography
                    variant="body2"
                    className="validation-card-label"
                    sx={{ color: "var(--text-color)" }}
                  >
                    Transaction:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--text-secondary)" }}
                  >
                    {selectedValidation.transactionName}
                  </Typography>
                </Box>
                <Box className="validation-card-item">
                  <Typography
                    variant="body2"
                    className="validation-card-label"
                    sx={{ color: "var(--text-color)" }}
                  >
                    Status:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        selectedValidation.status === "Fail"
                          ? "var(--error-color)"
                          : "var(--success-color)",
                    }}
                  >
                    {selectedValidation.status}
                  </Typography>
                </Box>
                <Box className="validation-card-item">
                  <Typography
                    variant="body2"
                    className="validation-card-label"
                    sx={{ color: "var(--text-color)" }}
                  >
                    Details:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--text-secondary)" }}
                  >
                    {selectedValidation.details}
                  </Typography>
                </Box>
                {selectedValidation.status !== "Pass" && (
                  <Box className="validation-card-item">
                    <Typography
                      variant="body2"
                      className="validation-card-label"
                      sx={{ color: "var(--text-color)" }}
                    >
                      Evidence:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "var(--text-secondary)" }}
                    >
                      {selectedValidation.evidence}
                    </Typography>
                  </Box>
                )}
                <Box className="validation-card-item">
                  <Typography
                    variant="body2"
                    className="validation-card-label"
                    sx={{ color: "var(--text-color)" }}
                  >
                    Last Run:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--text-secondary)" }}
                  >
                    {selectedValidation.lastRun}
                  </Typography>
                </Box>
                {selectedValidation.changeHistory?.length > 0 && (
                  <Box className="validation-card-item">
                    <Typography
                      variant="body2"
                      className="validation-card-label"
                      sx={{ color: "var(--text-color)" }}
                    >
                      Change History:
                    </Typography>
                    <Box component="ul" className="validation-card-list">
                      {selectedValidation.changeHistory
                        .slice(0, 3)
                        .map((change, index) => (
                          <Box
                            component="li"
                            key={index}
                            className="validation-card-list-item"
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "var(--text-secondary)" }}
                            >
                              {change.field}: {change.oldValue} →{" "}
                              {change.newValue} at {change.timestamp}
                            </Typography>
                          </Box>
                        ))}
                      {selectedValidation.changeHistory.length > 3 && (
                        <Typography
                          variant="body2"
                          className="validation-card-more"
                          sx={{ color: "var(--primary-color)" }}
                        >
                          +{selectedValidation.changeHistory.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ color: "var(--text-color)" }}
            >
              No validation details available for the selected ID.
            </Typography>
          )}
        </Box>
        {userRole === "admin" && (
          <Box sx={{ mb: 3, mt: "20px" }}>
            <Stepper
              activeStep={validationStep}
              alternativeLabel
              sx={{
                background: "var(--card-background)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--border-color)",
                p: "32px",
                "& .MuiStepLabel-label": {
                  color: "var(--text-color)",
                  fontFamily: "var(--font-family-base)",
                  fontWeight: 600,
                  fontSize: "1rem",
                },
                "& .MuiStepLabel-active": {
                  background: "var(--gradient-primary)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                },
                "& .MuiStepIcon-root": {
                  color: "var(--card-background)",
                  border: "2px solid var(--border-color)",
                  borderRadius: "50%",
                },
                "& .MuiStepIcon-active": {
                  color: "var(--primary-color)",
                  borderColor: "var(--primary-color)",
                },
                "& .MuiStepIcon-completed": {
                  color: "var(--success-color)",
                  borderColor: "var(--success-color)",
                },
              }}
            >
              <Step>
                <StepLabel>Rerun</StepLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="contained"
                    className="rerun-validations-button"
                    onClick={handleRerunValidations}
                    disabled={isValidating || timerActive}
                    sx={{
                      fontSize: "16px",
                      padding: "10px 20px",
                      background: "var(--gradient-primary)",
                      color: "var(--text-color)",
                      "&:hover": {
                        background: "var(--gradient-primary)",
                        transform: "translateY(-3px) scale(1.05)",
                        boxShadow: "var(--box-shadow-hover)",
                      },
                      "&:disabled": {
                        background: "var(--disabled-background)",
                        color: "var(--disabled-text)",
                      },
                    }}
                  >
                    {isValidating ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: "var(--primary-color)" }}
                      />
                    ) : (
                      "Rerun Validations"
                    )}
                  </Button>
                </Box>
              </Step>
              <Step>
                <StepLabel>View</StepLabel>
                <Button
                  variant="contained"
                  className="view-validations-button"
                  onClick={handleViewValidations}
                  disabled={
                    isFetchingValidations || validationStep < 1 || timerActive
                  }
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    padding: "10px 20px",
                    background: "var(--gradient-primary)",
                    color: "var(--text-color)",
                    animation:
                      validationStep === 1 && !timerActive
                        ? "pulse 2s infinite"
                        : "none",
                    "&:hover": {
                      background: "var(--gradient-primary)",
                      transform: "translateY(-3px) scale(1.05)",
                      boxShadow: "var(--box-shadow-hover)",
                    },
                    "&:disabled": {
                      background: "var(--disabled-background)",
                      color: "var(--disabled-text)",
                    },
                  }}
                >
                  {isFetchingValidations ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: "var(--primary-color)" }}
                    />
                  ) : (
                    "View Validations"
                  )}
                </Button>
              </Step>
            </Stepper>
          </Box>
        )}
        <Box
          className="transaction-content"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flex: 1,
            gap: "20px",
            overflow: "hidden",
          }}
        >
          <Box
            className="preview-data-container"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: "var(--card-background)",
              borderRadius: "var(--border-radius-lg)",
              border: "1px solid var(--border-color)",
              p: "24px",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <CircularProgress sx={{ color: "var(--primary-color)" }} />
              </Box>
            ) : (
              <>
                <ExtractedData
                  file={
                    selectedFileIndex !== null
                      ? documents[selectedFileIndex]
                      : null
                  }
                  pdfUrl={pdfUrl}
                  loading={loading}
                  error={error}
                  extractedData={getFinalizedData(extractedData)}
                  averageMonthlyImportAmount={averageMonthlyImportAmount}
                  setAverageMonthlyImportAmount={setAverageMonthlyImportAmount}
                  handleJsonChange={handleJsonChange}
                  userRole={userRole}
                  isSaved={isSaved}
                  transactionId={transactionId}
                  documentId={
                    selectedFileIndex !== null
                      ? documents[selectedFileIndex]?.id
                      : null
                  }
                  isDocumentManager={true}
                  onEditChange={setIsEditing}
                  theme={theme}
                />
                {userRole === "admin" &&
                  selectedFileIndex !== null &&
                  extractedData &&
                  !isSaved && (
                    <Box
                      className="update-button-container"
                      sx={{ mt: "20px" }}
                    >
                      <Button
                        className="update-button"
                        variant="contained"
                        onClick={handleUpdateData}
                        disabled={selectedFileIndex === null || !extractedData || isSaved}
                        sx={{
                          fontSize: "16px",
                          padding: "10px 20px",
                          background: "var(--gradient-primary)",
                          color: "var(--text-color)",
                          "&:hover": {
                            background: "var(--gradient-primary)",
                            transform: "translateY(-3px) scale(1.05)",
                            boxShadow: "var(--box-shadow-hover)",
                          },
                          "&:disabled": {
                            background: "var(--disabled-background)",
                            color: "var(--disabled-text)",
                          },
                        }}
                      >
                        Update Data
                      </Button>
                    </Box>
                  )}
              </>
            )}
          </Box>
        </Box>
        <Box
          className="files-container"
          sx={{
            mt: "20px",
            position: "relative",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: "10px",
              fontSize: "24px",
              fontWeight: 500,
              color: "var(--text-color)",
              fontFamily: "var(--font-family-heading)",
            }}
          >
            Files
          </Typography>
          <Box className="uploaded-files">
            {documents.map((file, index) => (
              <Box
                className={`uploaded-file-item ${
                  selectedFileIndex === index ? "selected" : ""
                }`}
                key={file.id}
                onClick={() => handleFileSelect(index)}
                role="button"
                tabIndex={0}
                aria-label={`Select document ${truncateFileName(file.name)}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleFileSelect(index);
                  }
                }}
              >
                <img
                  src={file.thumbnail || "/default-thumbnail.jpg"}
                  alt={file.name}
                />
                <Typography
                  variant="caption"
                  className="file-name"
                  sx={{ color: "var(--text-secondary)" }}
                >
                  {truncateFileName(file.name)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentManager;